import * as React from 'react';
import { Helmet } from 'react-helmet';
import Wan from '@app/api/wan/wan.actions';
import getAllGatewayIds from '@app/pages/wan/WanHelper';
import Site from '@app/api/site/site.actions';
import Server from '@app/api/gateway/gateway.actions';
import Controller from '@app/api/controller/controller.actions';
import { countChars, emptyObject } from '@app/lib/functions';
import { textInputRequiredOnly, isLengthWithinRange, isValidIP, isValidSubnet, ipInSubnet } from '@app/lib/validator';
import { IconHeading } from '@app/components/IconHeading';
import { Saver } from '@app/components/Saver';
import { truncateString } from '@app/utils/utils';
import WanIcon from '@app/bgimages/wans-black.svg';
import ACLProfile from '@app/api/aclprofile/aclprofile.actions';

import { ClusterIcon, TopologyIcon, LockIcon, DomainIcon, InfrastructureIcon, ExclamationCircleIcon, ServiceCatalogIcon } from '@patternfly/react-icons';
import { Flex, FlexItem, PageSection, Title, Chip, ChipGroup, FormGroup, TextInput, Select, SelectOption, SelectVariant, TextArea, Spinner } from "@patternfly/react-core";
import { Table, TableHeader, TableBody, TableVariant, cancelCellEdits, validateCellEdits, applyCellEdits, EditableTextCell, EditableSelectInputCell } from '@patternfly/react-table';
import { FormGroupSpacer } from '../../components/forms/pf/form-group-spacer.component';
import loaderLogo from '@app/assets/images/logo/CloudAccess-Logo-BlkCloud.jpg';

import './Wan.css';
import Page from '@app/components/Page';
import { Card, CardBody, CardHeader, Col, Input, Row } from 'reactstrap';

import Loader from '@app/components/Loader';
import { RouteComponentProps } from 'react-router-dom';

interface WanFormProps extends RouteComponentProps<{ id: string }> {}

interface WanFormState {
  id: string;
  name: string;
  description: string;
  isEncrypted: boolean;
  sites: any[];
  wans: any[];
  wan: any;
  servers: any[];
  controllers: any[];
  siteSubnets: any[];
  origAccessibleIps: any[];
  activeController: any;
  activeGateway: any;
  standbyController: any;
  standbyGateway: any;
  isActiveControllerOpen: boolean;
  isActiveGatewayOpen: boolean;
  isStandbyControllerOpen: boolean;
  isStandbyGatewayOpen: boolean;
  failoverEnabled: boolean;
  failoverEnabledCheckboxDisabled: boolean;
  isACLProfileOpen: boolean;
  aclProfiles: any[];
  aclProfileOptions: any[];
  message: string;
  messageType: string;
  error: string;
  dataLoading: boolean;
  editRowMode: boolean;
  isIpAccessLevelOpen: boolean;
  mode: string;
  standbyControllers?: any[];
  // Legacy DHCP state is retained for existing records but is no longer configurable here.
  dhcpEnabled: boolean;
  dhcpIpAddress: string;
  dhcpNetmask: string;
  dhcpRangeStart: string;
  dhcpRangeEnd: string;
  dhcpDefaultLease: number;
  dhcpMaxLease: number;
  dhcpDescription: string;
  // DHCP validation state
  dhcpIpAddressValid: boolean;
  dhcpRangeStartValid: boolean;
  dhcpRangeEndValid: boolean;
}

class WanForm extends React.Component<WanFormProps, WanFormState> {
  context: any;
  
  constructor(props: WanFormProps) {
    super(props);

    const { id } = this.props.match.params; 

    this.state = {
      id: id,
      name: "",
      description: "",
      isEncrypted: false,
      sites: [],
      wans: [],
      wan: {},
      servers: [],
      controllers: [],
      siteSubnets: [],
      origAccessibleIps: [],
      activeController: {},
      activeGateway: {},
      standbyController: {},
      standbyGateway: {},      
      isActiveControllerOpen: false,
      isActiveGatewayOpen: false,
      isStandbyControllerOpen: false,
      isStandbyGatewayOpen: false,
      failoverEnabled: false,
      failoverEnabledCheckboxDisabled: true,
      isACLProfileOpen: false,
      aclProfiles: [],
      aclProfileOptions: [],
      message: "",
      messageType: "",
      error: "",
      dataLoading: true,
      editRowMode: false,
      isIpAccessLevelOpen: false,
      mode: id ? "edit" : "new",
      // Initialize DHCP settings
      dhcpEnabled: true,
      dhcpIpAddress: '',
      dhcpNetmask: '/24',
      dhcpRangeStart: '',
      dhcpRangeEnd: '',
      dhcpDefaultLease: 3600,
      dhcpMaxLease: 7200,
      dhcpDescription: '',
      // Initialize DHCP validation state
      dhcpIpAddressValid: true,
      dhcpRangeStartValid: true,
      dhcpRangeEndValid: true
    }
  }

  componentDidMount = () => {

    try {
      // Determine the correct mode based on URL path
      const path = this.props.location.pathname;

      
      // If URL contains '/edit/', it's edit mode
      const isEditMode = path.includes('/edit/');

      
      // Update the mode in state if needed
      if (isEditMode && this.state.mode !== 'edit') {

        this.setState({ mode: 'edit', dataLoading: true }, () => {
          this.loadWan();
        });
      } else {
        this.setState({ dataLoading: true });
        this.loadWan();
      }
    }
    catch (error) {
      this.setState(() => ({
        message: "There was an error loading the pWAN",
        dataLoading: false
      }));
    }
  }

  loadWan = async () => {
    const [controllers, wans, servers, allAclProfiles] = await Promise.all([
      Controller.getAll(),
      Wan.getAll(),
      Server.getAll(),
      ACLProfile.getAll(),
    ]);
    const notServerless = controllers.filter(c => c.name !== "Serverless");
    let aclProfileOptions = [];

    if (allAclProfiles) {
      aclProfileOptions = allAclProfiles.map(({ id, inbound_rules, outbound_rules, ...profiles }) => profiles);
      aclProfileOptions = aclProfileOptions.map(profile => {
        return {
          value: profile.name,
          description: truncateString(profile.description, 80),
          disabled: false
        }
      });
    }

    // @ts-ignore
    const mode = this.state.mode;


    if (mode === "new") {
      this.setState(() => ({
        wans: wans,
        controllers: notServerless,
        aclProfileOptions: [...aclProfileOptions],
        dataLoading: false
      }));
    }
    else if (mode === "edit") {
      // Get ID from URL path for edit mode
      const path = this.props.location.pathname;
      const pathParts = path.split('/');
      const id = pathParts[pathParts.length - 1];

      
      // @ts-ignore
      this.setState({ id }, () => {

        this.loadWanDataById(id, notServerless, wans, servers, aclProfileOptions);
      });
    }
  }
  
  loadWanDataById = async (id, notServerless, wans, servers, aclProfileOptions) => {
    const [wan, sites, assignedAclProfiles] = await Promise.all([
      Wan.get(id),
      Site.getSitesByWan(id),
      ACLProfile.getAssignedACLs(id),
    ]);

  
    if (!wan) {

      this.props.history.push("/notfound");
      return;
    }

    let wanGateways = getAllGatewayIds(wans);
    wanGateways = wanGateways.filter(wanId => wanId !== wan.activeGatewayId);
    const siteSubnets = sites.map(site => { return this.getRow(site, false); });
    const origAccessibleIps = sites.map(site => { return { id: site.id, accessibleIps: site.accessibleIps, ipAccessLevel: site.ipAccessLevel }});

    // Active Controller
    let activeController = notServerless.find(c => c.id === wan.activeGatewayId) || {};
    const activeGateway = servers.find(s => s.id === wan.activeServerId);
    
    // Add null check for activeGateway
    let hasActiveGatewayError = false;
    if (!activeGateway || !activeGateway.Gateway) {
      hasActiveGatewayError = true;

      activeController = {};
    } else {
      const activeGateways = activeGateway.Gateway.Servers.filter(s => {
        return s.gatewayId === wan.activeGatewayId && !wanGateways.includes(wan.activeGatewayId) && (s.Tunnels?.length === 0 || s.id === wan.activeServerId)
      });
      activeController.Servers = [...activeGateways];
    }

    // Standby Controller
    let standbyController = notServerless.find(c => c.id === wan.standbyGatewayId);
    const standbyGateway = standbyController ? servers.find(s => s.id === wan.standbyServerId) : "";
    
    // Add null check for standbyGateway
    let standbyGateways = [];
    if (standbyController && standbyGateway && standbyGateway.Gateway) {
      // @ts-ignore
      standbyGateways = standbyGateway.Gateway.Servers.filter(s => { 
        return s.gatewayId === wan.standbyGatewayId && !wanGateways.includes(wan.standbyGatewayId) && (s.Tunnels?.length === 0 || s.id === wan.standbyServerId)
      });
      standbyController.Servers = [...standbyGateways];
    } else if (standbyController) {
      standbyController.Servers = [];
    }
    
    const standbyControllers = notServerless.filter(c => { 
      return c.name !== activeController.name && c.name !== 'Serverless'
    });

    let aclProfiles = assignedAclProfiles;
    // @ts-ignore
    aclProfiles = aclProfiles.map(acl => { return acl.name });

    // Check if this is an L2 WAN and load DHCP settings from site configurations


    let dhcpSettings = {
      dhcpEnabled: true,
      dhcpIpAddress: '',
      dhcpNetmask: '/24',
      dhcpRangeStart: '',
      dhcpRangeEnd: '',
      dhcpDefaultLease: 3600,
      dhcpMaxLease: 7200,
      dhcpDescription: ''
    };

    // Use DHCP data from GraphQL
    if (wan?.DHCP) {

      dhcpSettings = {
        dhcpEnabled: true,
        dhcpIpAddress: wan.DHCP.subnet?.split('/')[0] || '',
        dhcpNetmask: wan.DHCP.subnet?.includes('/') ? `/${wan.DHCP.subnet.split('/')[1]}` : '/24',
        dhcpRangeStart: wan.DHCP.range_start || '',
        dhcpRangeEnd: wan.DHCP.range_end || '',
        dhcpDefaultLease: wan.DHCP.default_lease_seconds || 3600,
        dhcpMaxLease: wan.DHCP.max_lease_seconds || 7200,
        dhcpDescription: wan.DHCP.description || ''
      };
    } else {

    }


    
    const finalState = {
      name: wan.name,
      description: wan.description || "",
      isEncrypted: wan.isEncrypted,
      sites: sites,
      wans: wans,
      wan: wan,
      servers: servers,
      aclProfiles: [...aclProfiles],
      aclProfileOptions: [...aclProfileOptions],
      failoverEnabledCheckboxDisabled: false,
      origAccessibleIps: origAccessibleIps,
      controllers: [...notServerless],
      standbyControllers: [...standbyControllers],
      siteSubnets: [...siteSubnets],
      activeController: {...activeController},
      activeGateway: hasActiveGatewayError ? {} : {...activeGateway},
      // @ts-ignore
      standbyController: standbyController ? {...standbyController} : {},
      // @ts-ignore
      standbyGateway: standbyGateway ? {...standbyGateway} : {},
      failoverEnabled: standbyController && standbyGateway,
      message: hasActiveGatewayError ? "Could not find active gateway" : "",
      // Load DHCP settings from GraphQL
      ...dhcpSettings,
      dataLoading: false
    };



    // @ts-ignore
    this.setState(finalState);
  }

  onACLProfileSelect = (event, selection) => {
    // @ts-ignore
    const { aclProfiles } = this.state;

    if (aclProfiles.includes(selection)) {
      this.setState(prev => ({
        // @ts-ignore
        aclProfiles: prev.aclProfiles.filter(item => item !== selection)
      }));
    }
    else {
      this.setState(prev => ({
        // @ts-ignore
        aclProfiles: [...prev.aclProfiles, selection] 
      }));
    }
  };

  clearACLProfileSelection = () => {
    this.setState({
      aclProfiles: [],
      isACLProfileOpen: false
    });
  };

  chipGroupComponent = () => {
    // @ts-ignore
    const { aclProfiles } = this.state;

    return (
      <ChipGroup>
        {(aclProfiles || []).map((currentChip, index) => (
          <Chip
            isReadOnly={index === 0 ? true : false}
            key={currentChip}
            onClick={event => this.onACLProfileSelect(event, currentChip)}
          >
            {currentChip}
          </Chip>
        ))}
      </ChipGroup>
    );
  };

  getSiteSubnetColumns = () => {
    return ['LAN IP', 'Netmask', 'Accessible IPs', 'IP Addresses'];
  }

  getCardClass = (section, enabled) => {
    return enabled ? section + "-form-row card-enabled" : section + "-form-row card-disabled";
  }

  updateEditableRows = async (evt, type, isEditable, rowIndex, validationErrors) => {
    // @ts-ignore
    let newRows = Array.from(this.state.siteSubnets);

    if (validationErrors && Object.keys(validationErrors).length) {
      newRows[rowIndex] = validateCellEdits(newRows[rowIndex], type, validationErrors);
      this.setState({ siteSubnets: newRows });
      return;
    }
    
    if (type === 'edit' && Object.keys(validationErrors).length === 0) {
      this.setState({ editRowMode: true });
      newRows[rowIndex] = applyCellEdits(newRows[rowIndex], type);
      return;
    }

    if (type === 'cancel') {
      // @ts-ignore
      const { origAccessibleIps } = this.state;
      // @ts-ignore
      if (this.state.error !== "") {
        // @ts-ignore
        const siteId = newRows[rowIndex].cells[4].props.value;
        const origObj = origAccessibleIps.find(ip => ip.id === siteId);
        // @ts-ignore
        newRows[rowIndex].cells[2].props.value = origObj.ipAccessLevel;
        // @ts-ignore
        newRows[rowIndex].cells[3].props.value = origObj.accessibleIps;
      }
      newRows[rowIndex] = cancelCellEdits(newRows[rowIndex]);
      this.setState({ siteSubnets: newRows, editRowMode: false, error: "" });
      return;
    }

    if (type === 'save') {
      newRows[rowIndex] = applyCellEdits(newRows[rowIndex], type);

      let ipCheck = { isValid: true, error: "" };
      const row = newRows[rowIndex];
      // @ts-ignore
      const lanSubnet = row.cells[0].props.value + row.cells[1].props.value;
      // @ts-ignore
      const ipAccessLevel = row.cells[2].props.value;
      // @ts-ignore
      const accessibleIps = row.cells[3].props.value.replaceAll(" ", "");

      // Accessible IPs must be a comma separated list of valid IP addresses and/or 
      // subnets in CIDR format. Only applicable when ipAccessLevel is set to "Specific"
      if (ipAccessLevel === "Specific") {
        if (accessibleIps === "" || countChars(accessibleIps, ",") === 0) {
          ipCheck = this.validateAccessibleIp(accessibleIps, lanSubnet);
        }
        else {  
          const csv = accessibleIps.split(',');
          for (var i = 0; i < csv.length; i++) {
            ipCheck = this.validateAccessibleIp(csv[i], lanSubnet);
            if (!ipCheck.isValid) { break; }
          }   
        }
      }

      // Remove any spaces from comma separated IPs
    // @ts-ignore
      row.cells[3].props.value = row.cells[3].props.value.replaceAll(" ", "");

      if (!ipCheck.isValid) {
        newRows[rowIndex] = applyCellEdits(newRows[rowIndex], type);
        this.setState({ editRowMode: true, error: ipCheck.error });
      }
      else {
        this.setState({ siteSubnets: [...newRows], editRowMode: false, error: "" });
      }
    }
  }

  validateAccessibleIp = (ip, lanSubnet) => {
    if (!isValidIP(ip) && !isValidSubnet(ip)) {
      return { isValid: false, error: ip + " must be a valid IP or CIDR format" }
    }
    if (!ipInSubnet(ip, lanSubnet)) {
      return { isValid: false, error: ip + " must exist in LAN subnet " + lanSubnet }
    }

    return { isValid: true, error: "" }
  }

  getRow = (site, isEditable) => {
    var cells = [];
    let accessibleIpsDisabled = false;

    if (site.ipAccessLevel !== "Specific") {
      accessibleIpsDisabled = true;
    }

    cells.push(this.getTextCell(site.SiteLans[0], "lanSegmentIp4", true, false));
    cells.push(this.getTextCell(site.SiteLans[0], "lanCidrIp4", true, false));
    cells.push(this.getSelectCell(site, "ipAccessLevel", false));
    cells.push(this.getTextCell(site, "accessibleIps", false, accessibleIpsDisabled));
    cells.push(this.getTextCell(site, "id", true, false));

    const row = {
      rowEditValidationRules: [],
      cells: cells
    }

    // @ts-ignore
    row.isEditable = isEditable;
 
    return row;
  } 

  getSelectCell = (site, columnName, readOnly) => {
    const cell = {
      title: (value, rowIndex, cellIndex, props) => (
        <EditableSelectInputCell
          value={value}
          rowIndex={rowIndex}
          cellIndex={cellIndex}
          props={props}
          onSelect={this.onIpAccessLevelSelect}
          isOpen={props.isSelectOpen}
          options={props.options.map((option, index) => {
            return (
              <SelectOption 
                id={'ip-access-level-' + index} key={index} value={option.value} 
                isPlaceholder={option.isPlaceholder} 
              />
            );
          })}
          onToggle={isOpen => {
            this.onIpAccessLevelToggle(isOpen, rowIndex, cellIndex);
          }}
          selections={props.selected}
        />
      ),
      props: {
        value: site[columnName],
        name: columnName,
        // @ts-ignore
        isSelectOpen: props.isSelectOpen || false,
        // @ts-ignore
        selected: props.selected && props.selected.length ? props.selected : site[columnName],
        options: [
          { value: 'All' },
          { value: 'Specific' },
          { value: 'None' }
        ]
      }
    }

    return cell;
  }

  getTextCell = (site, columnName, readOnly, isDisabled) => {
    if (readOnly) {
      return { 
        title: site?.[columnName] || "",
        props: {
          value: site?.[columnName],
          name: columnName
        }
      };
    }
    
    const cell = {
      title: (value, rowIndex, cellIndex, props) => (
        <EditableTextCell
          value={value || ""}
          rowIndex={rowIndex}
          cellIndex={cellIndex}
          props={props}
          isDisabled={isDisabled}
          handleTextInputChange={this.handleTextInputChange}
          inputAriaLabel={columnName}
        />
      ),
      props: {
        value: site[columnName],
        name: columnName
      }
    }

    return cell;
  }

  onIpAccessLevelSelect = (newValue, evt, rowIndex, cellIndex, isPlaceholder) => {
    // @ts-ignore
    const newRows = Array.from(this.state.siteSubnets);
    // @ts-ignore
    const newCellProps = newRows[rowIndex].cells[cellIndex].props;
    let accessibleIpsDisabled = false;
    // @ts-ignore
    let accessibleIps = newRows[rowIndex].cells[3].props.value;

    if (isPlaceholder) {
      newCellProps.editableValue = [];
      newCellProps.selected = [];
    } 
    else {
      if (newCellProps.editableValue === undefined) {
        newCellProps.editableValue = [];
      }

      let newSelected = Array.from(newCellProps.selected);
      newSelected = newValue;
      newCellProps.editableValue = newSelected;
      newCellProps.selected = newSelected;
    }

    if (newValue !== "Specific") {
      accessibleIpsDisabled = true;
      accessibleIps = "";
    }

    // A hack to be able to conditionally disable the accessibleIps text field when ipAccessLevel !== "Specific"
    // @ts-ignore
    newRows[rowIndex].cells[3] = this.getTextCell(
      { accessibleIps: accessibleIps }, "accessibleIps", false, accessibleIpsDisabled
    );

    // Close the ipAccessLevel dropdown
    // @ts-ignore
    newRows[rowIndex].cells[2].props.isSelectOpen = false;

    this.setState({
      siteSubnets: [...newRows]
    });
  }

  onIpAccessLevelToggle = (isOpen, rowIndex, cellIndex) => {
    // @ts-ignore
    let newRows = Array.from(this.state.siteSubnets);
    // @ts-ignore
    newRows[rowIndex].cells[cellIndex].props.isSelectOpen = isOpen;
    this.setState({
      siteSubnets: [...newRows]
    });
  }

  onSelectToggle = (isOpen, isOpenStateVar) => {
    // @ts-ignore
    this.setState({ [isOpenStateVar]: isOpen });
  }
  
  clearActiveController = (stateObject: string, isOpenStateVar: string) => {
    // @ts-ignore
    this.setState({
      [stateObject]: {},
      [isOpenStateVar]: false
    });
  }

  onActiveControllerSelect = (event, selection, isPlaceholder) => {
    if (isPlaceholder) {
      // @ts-ignore
      this.clearActiveController("activeController", "isActiveControllerOpen");
    }
    else {
      // @ts-ignore
      const { controllers, wans, wan, standbyController } = this.state;
      let wanGateways = getAllGatewayIds(wans);
      const activeController = controllers.find(c => c.name === selection);
      const activeGateways = activeController.Servers.filter(s => {
        return (s.gatewayId === activeController?.id && s.Tunnels?.length === 0 && !wanGateways.includes(s.id)) || (wan.activeServerId && s.id === wan.activeServerId);
      });
      const standbyControllers = controllers.filter(c => { return c.name !== selection });

      this.setState(prevState => ({
        activeController: {
          // @ts-ignore
          ...prevState.activeController,
          id: activeController.id,
          name: selection,
          Servers: [...activeGateways]
        },
        failoverEnabledCheckboxDisabled: false,
        activeGateway: {},
        standbyControllers: [...standbyControllers],
        // @ts-ignore
        standbyController: selection === standbyController.name ? {} : { ...prevState.standbyController },
        // @ts-ignore
        standbyGateway: selection === standbyController.name ? {} : { ...prevState.standbyGateway },
        isActiveControllerOpen: false 
      }));
    }
  }

  onStandbyControllerSelect = (event, selection, isPlaceholder) => {
    if (isPlaceholder) {
      // @ts-ignore
      this.clearActiveController("standbyController", "isStandbyControllerOpen");
    }
    else {
      // @ts-ignore
      const { controllers, wans, wan } = this.state;
      let wanGateways = getAllGatewayIds(wans);
      const standbyController = controllers.find(c => c.name === selection);
      const standbyGateways = standbyController.Servers.filter(s => { 
        return (s.Tunnels?.length === 0 && !wanGateways.includes(s.id)) || (wan.standbyServerId && s.id === wan.standbyServerId) 
      });

      this.setState(prevState => ({
        standbyGateway: {},
        standbyController: {
          // @ts-ignore
          ...prevState.standbyController,
          id: standbyController.id,
          name: selection,
          Servers: standbyGateways
        },
        isStandbyControllerOpen: false 
      }));
    }
  }

  onActiveGatewaySelect = (event, selection, isPlaceholder) => {
    if (isPlaceholder) {
      // @ts-ignore
      this.clearActiveController("activeGateway", "isActiveGatewayOpen");
    }
    else {
      // @ts-ignore
      const { activeController } = this.state;
      const activeGateway = activeController.Servers.find(s => s.ip4Address === selection);

      this.setState(() => ({
        activeGateway: {...activeGateway},
        isActiveGatewayOpen: false 
      }));
    }
  }

  onStandbyGatewaySelect = (event, selection, isPlaceholder) => {
    if (isPlaceholder) {
      // @ts-ignore
      this.clearActiveController("standbyGateway", "isStandbyGatewayOpen");
    }
    else {
      // @ts-ignore
      const { standbyController } = this.state;
      const standbyGateway = standbyController.Servers.find(s => s.ip4Address === selection);

      this.setState(() => ({
        standbyGateway: {...standbyGateway},
        isStandbyGatewayOpen: false 
      }));
    }
  }

  handleFailoverEnabled = (checked) => {
    // @ts-ignore
    const { standbyController, standbyGateway } = this.state; 
    this.setState({ 
      failoverEnabled: checked,
      standbyController: checked ? {...standbyController} : {},
      standbyGateway: checked ? {...standbyGateway} : {}
    });
  }

  handleTextInputChange = (newValue, evt, rowIndex, cellIndex) => {
    // @ts-ignore
    let newRows = Array.from(this.state.siteSubnets);
    // @ts-ignore
    newRows[rowIndex].cells[cellIndex].props.editableValue = newValue;
    this.setState(() => ({
      siteSubnets: newRows
    }));
  }

  handleChange = (name, value) => {
    // If value is an event object (like from an input field), extract the actual value
    const newValue = value && value.target ? value.target.value : value;
    
    // @ts-ignore - Ignore TypeScript error related to dynamic property
    this.setState({ [name]: newValue });
  }

  handleEncryptionEnabled = (checked) => {
    this.setState({ 
      isEncrypted: checked 
    });
  }

  handleDhcpChange = (field, event) => {
    const value = event.target.value;
    if (field === 'dhcpIpAddress') {
      this.setState({ 
        dhcpIpAddress: value,
        dhcpIpAddressValid: value === '' || isValidIP(value)
      }, () => {
        this.calculateDhcpRange();
      });
    } else if (field === 'dhcpNetmask') {
      this.setState({ dhcpNetmask: value }, () => {
        this.calculateDhcpRange();
      });
    } else if (field === 'dhcpRangeStart') {
      this.setState({ 
        dhcpRangeStart: value,
        dhcpRangeStartValid: value === '' || isValidIP(value)
      });
    } else if (field === 'dhcpRangeEnd') {
      this.setState({ 
        dhcpRangeEnd: value,
        dhcpRangeEndValid: value === '' || isValidIP(value)
      });
    } else if (field === 'dhcpDefaultLease') {
      this.setState({ dhcpDefaultLease: parseInt(value) || 3600 });
    } else if (field === 'dhcpMaxLease') {
      this.setState({ dhcpMaxLease: parseInt(value) || 7200 });
    } else if (field === 'dhcpDescription') {
      this.setState({ dhcpDescription: value });
    }
  }

  calculateDhcpRange = () => {
    const { dhcpIpAddress, dhcpNetmask } = this.state;
    
    if (dhcpIpAddress && dhcpNetmask) {
      try {
        // Convert netmask from /24 format to number
        const prefixLength = parseInt(dhcpNetmask.replace('/', ''));
        
        // Split IP into octets
        const octets = dhcpIpAddress.split('.').map(num => parseInt(num));
        
        if (octets.length === 4) {
          // Calculate subnet mask
          const mask = (0xFFFFFFFF << (32 - prefixLength)) >>> 0;
          
          // Convert IP to 32-bit integer
          const ipInt = (octets[0] << 24) + (octets[1] << 16) + (octets[2] << 8) + octets[3];
          
          // Apply mask to get network address
          const networkInt = (ipInt & mask) >>> 0;
          
          // Calculate the number of host addresses
          const hostBits = 32 - prefixLength;
          
          // Prevent calculation for very large subnets (less than /8)
          if (prefixLength < 8) {
            // For very large subnets, use a reasonable default range
            const baseIp = `${octets[0]}.${octets[1]}.${octets[2]}`;
            const startIp = `${baseIp}.1`;
            const endIp = `${baseIp}.254`;
            
            this.setState({
              dhcpRangeStart: startIp,
              dhcpRangeEnd: endIp
            });
            return;
          }
          
          const totalHosts = Math.pow(2, hostBits);
          
          // First usable IP (network + 1)
          const firstUsableInt = networkInt + 1;
          
          // Last usable IP (broadcast - 1)
          const lastUsableInt = networkInt + totalHosts - 2;
          
          // Convert back to dotted decimal
          const firstUsableOctets = [
            (firstUsableInt >>> 24) & 0xFF,
            (firstUsableInt >>> 16) & 0xFF,
            (firstUsableInt >>> 8) & 0xFF,
            firstUsableInt & 0xFF
          ];
          
          const lastUsableOctets = [
            (lastUsableInt >>> 24) & 0xFF,
            (lastUsableInt >>> 16) & 0xFF,
            (lastUsableInt >>> 8) & 0xFF,
            lastUsableInt & 0xFF
          ];
          
          const startIp = firstUsableOctets.join('.');
          const endIp = lastUsableOctets.join('.');
          
          this.setState({
            dhcpRangeStart: startIp,
            dhcpRangeEnd: endIp
          });
        }
      } catch (error) {
        console.error('Error calculating DHCP range:', error);
      }
    }
  }

  getActiveControllerOptions = () => {
    // @ts-ignore
    const { controllers } = this.state;
    return controllers.map(controller => {
      return <SelectOption key={controller.id} id={controller.id} value={controller.name} />
    });
  }

  getActiveGatewayOptions = () => {
    // @ts-ignore
    const { activeController } = this.state;
    if (!activeController.Servers) {
      return [];
    }
    return activeController.Servers.map(gateway => {
      return <SelectOption key={gateway.id} id={gateway.id} value={gateway.ip4Address} />
    });
  }

  getStandbyControllerOptions = () => {
    // @ts-ignore
    const { standbyControllers, failoverEnabled } = this.state;

    if (failoverEnabled) {
      return standbyControllers.map(controller => {
        return <SelectOption key={controller.id} id={controller.id} value={controller.name} />
      });
    }
    return [];
  }

  getStandbyGatewayOptions = () => {
    // @ts-ignore
    const { standbyController, failoverEnabled } = this.state;

    if (failoverEnabled) {
      if (!standbyController.Servers) {
        return [];
      }
      return standbyController.Servers.map(gateway => {
        return <SelectOption key={gateway.id} id={gateway.id} value={gateway.ip4Address} />
      });
    }
  }

  handleCancel = () => {
    // @ts-ignore
    if (this.state.id) {
      // @ts-ignore
      this.props.history.push('/wan/' + this.state.id)
    }
    else {
      this.props.history.push('/wans')
    }
  }

  handleSubmit = async () => {

    // @ts-ignore
    if (this.state.mode === "edit") {

      this.updateWan();
    }
    // @ts-ignore
    else if (this.state.mode === "new") {

      this.createWan();
    }
    else {

    }
  }

  calculateNetworkCidr = (ipAddress: string, netmask: string) => {
    try {
      // Convert netmask from /24 format to number
      const prefixLength = parseInt(netmask.replace('/', ''));
      
      // Split IP into octets
      const octets = ipAddress.split('.').map(num => parseInt(num));
      
      // Calculate subnet mask
      const mask = (0xFFFFFFFF << (32 - prefixLength)) >>> 0;
      
      // Convert IP to 32-bit integer
      const ipInt = (octets[0] << 24) + (octets[1] << 16) + (octets[2] << 8) + octets[3];
      
      // Apply mask to get network address
      const networkInt = (ipInt & mask) >>> 0;
      
      // Convert back to dotted decimal
      const networkOctets = [
        (networkInt >>> 24) & 0xFF,
        (networkInt >>> 16) & 0xFF,
        (networkInt >>> 8) & 0xFF,
        networkInt & 0xFF
      ];
      
      return `${networkOctets.join('.')}/${prefixLength}`;
    } catch (error) {

      return '10.0.0.0/24'; // fallback
    }
  }

  getPayloadFromState = () => {

    // @ts-ignore
    const { name, description, isEncrypted, activeController, activeGateway, standbyController, standbyGateway, siteSubnets, wan } = this.state;


    const payload = {
      name: name, 
      description: description, 
      isEncrypted: isEncrypted, 
      activeGatewayId: activeController.id,
      activeServerId: activeGateway.id,
      siteSubnets: siteSubnets,

      // Existing records retain their type; new and previously unset records default to Layer 3.
      type: wan?.type ?? 3,
    };

    if (standbyController && standbyController.id && standbyGateway && standbyGateway.id) {
      // @ts-ignore
      payload.standbyGatewayId = standbyController.id;
      // @ts-ignore
      payload.standbyServerId = standbyGateway.id;

    }
    else {
      // @ts-ignore
      payload.standbyGatewayId = null;
      // @ts-ignore
      payload.standbyServerId = null;
  
    }

    return payload;
  }

  createWan = async () => {
    try {
      // @ts-ignore
      const { aclProfiles } = this.state;
          const payload = this.getPayloadFromState();
      let result = await Wan.create(payload);
      await ACLProfile.setWanAssignments(aclProfiles, result.id);
      this.props.history.push('/wan/' + result.id);
    }
    catch (error) {
      this.setState(() => ({
        message: "Unable to create pWAN configuration"
      }));
    }
  }

  updateWan = async () => {
    try {
      // @ts-ignore
      const { id, sites, aclProfiles } = this.state;

      const payload = this.getPayloadFromState();

      // Update the WAN in GraphQL
      let result = await Wan.update(id, payload, sites);
      
      await ACLProfile.setWanAssignments(aclProfiles, id);
      
      this.props.history.push('/wan/' + id);
    }
    catch (error) {

      this.setState(() => ({
        message: "Unable to update pWAN configuration"
      }));
    }
  }

  render() {
    // @ts-ignore
    const { name, description, isEncrypted, siteSubnets, activeController, activeGateway, standbyController, standbyGateway, aclProfiles, aclProfileOptions, isActiveControllerOpen, isActiveGatewayOpen, isStandbyControllerOpen, isStandbyGatewayOpen, isACLProfileOpen, failoverEnabled, failoverEnabledCheckboxDisabled, editRowMode, dataLoading, error, mode
    } = this.state;
    const encryptionCardClass = this.getCardClass("wan-settings", isEncrypted);
    const aclProfileCardClass = this.getCardClass("wan-settings", aclProfiles.length > 0);
    const activeControllers = this.getActiveControllerOptions();
    const activeGateways = this.getActiveGatewayOptions();
    const standbyControllers = this.getStandbyControllerOptions();
    const standbyGateways = this.getStandbyGatewayOptions();
    const descriptionErrorState = description !== "" ? isLengthWithinRange(description, 1, 255) ? { validated: 'default', errorText: "" } : { validated: 'error', errorText: " Max 255 characters allowed" } : { validated: 'default', errorText: "" };

    const { theme } = this.context;
    const formInvalid = !name 
      || emptyObject(activeController) 
      || emptyObject(activeGateway) 
      || editRowMode 
      || descriptionErrorState.validated === 'error'
      || failoverEnabled && (emptyObject(standbyController) || emptyObject(standbyGateway));

    return (
      <React.Fragment> 
        {dataLoading ? (
          <Loader />
        ) : (
          <Page
            tag="div"
            className={`cr-page px-3 pt-2 wans-form position-relative ${theme}`}
            title=""
            breadcrumbs={[
              { name: <a href="/wans">pWANs</a>, active: false },
              // @ts-ignore
              { name: this.state.mode === "edit" ? "Edit pWAN" : "New pWAN", active: true },
            ]}
            actionButton={<></>}
          >
            <>
              <Row>
                <Col>
                  <div className="p-3 h-100 sites-card-wrapper">
                    {/* First Row - 5 Cards */}
                    <Row className="w-100 mb-1">
                      <Col lg={2} md={2} sm={12} xs={12} className="mb-2">
                        <Card className="w-100 h-100 form-section-card">
                          <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent preserve-pwan-text">
                            <div className="w-100">
                              <IconHeading icon={<ServiceCatalogIcon className="icon-medium"/>} heading="pWAN Details" />
                            </div>
                          </CardHeader>
                          <CardBody>
                            <FormGroup label="pWAN Name:" isRequired fieldId="name" className="form-item"
                                helperTextInvalid=" Required field" helperTextInvalidIcon={<ExclamationCircleIcon />} validated={textInputRequiredOnly(name)}>
                                <TextInput
                                  isRequired type="text" id="name" name="name" autoComplete="new-password" className="form-control"
                                  value={name} onChange={(e) => this.handleChange("name", e)} validated={textInputRequiredOnly(name)}
                                />
                            </FormGroup>
                            <FormGroup label="Description:" fieldId="description" className="form-item"
                              // @ts-ignore
                              helperTextInvalid={descriptionErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={descriptionErrorState.validated}>
                              <TextArea 
                                rows={3} id="description" name="description" autoComplete="new-password" className="form-control"
                                // @ts-ignore
                                value={description} onChange={(e) => this.handleChange("description", e)} validated={descriptionErrorState.validated}
                                aria-label="pWAN description" 
                              />
                            </FormGroup>
                          </CardBody>
                        </Card>
                      </Col>

                      <Col lg={2} md={2} sm={12} xs={12} className="mb-2">
                        <Card className="w-100 h-100 form-section-card">
                          <CardHeader>
                            <div className="w-100 d-flex justify-content-between">
                              <div className="w-100">  
                                <IconHeading 
                                  icon={<InfrastructureIcon className="icon-medium" />} heading="Controller" enableText="Enable Failover"
                                  showEnable={true} handleEnable={this.handleFailoverEnabled} enabled={failoverEnabled} checkboxDisabled={failoverEnabledCheckboxDisabled}
                                />
                              </div>
                            </div>
                          </CardHeader>
                          <CardBody>
                            <FormGroup label="Active Controller" isRequired={true} fieldId="active-controller-toggle" className="form-item"
                              helperTextInvalid=" Required field" helperTextInvalidIcon={<ExclamationCircleIcon />} validated={textInputRequiredOnly(activeController.name)}>
                              <Input
                                type="select" 
                                value={activeController.name || ''}
                                // @ts-ignore
                                onChange={(e, selection, isPlaceholder) => this.onActiveControllerSelect(e, e.target.value, isPlaceholder)}
                                onClick={(e) => this.onSelectToggle(e, "isActiveControllerOpen")}
                                open={isActiveControllerOpen} 
                              >
                                {activeControllers &&
                                <>
                                  <option key="default" value="">Select...</option>
                                  {activeControllers.map((activeController, index) => (
                                      <option key={activeController.key || index} value={activeController.props.value}>
                                          {activeController.props.value}
                                      </option>
                                  ))}
                                </>
                                }
                              </Input>
                            </FormGroup>

                            <FormGroup label="Standby Controller" isRequired={failoverEnabled} fieldId="standby-controller-toggle" className="form-item"
                              helperTextInvalid=" Required field" helperTextInvalidIcon={<ExclamationCircleIcon />} validated={failoverEnabled ? textInputRequiredOnly(standbyController.name) : 'default'}>
                              <Input
                                type="select" 
                                value={standbyController.name || ''}
                                // @ts-ignore
                                onChange={(e, selection, isPlaceholder) => this.onStandbyControllerSelect(e, e.target.value, isPlaceholder)}
                                onClick={(e) => this.onSelectToggle(e, "isStandbyControllerOpen")}
                                open={isStandbyControllerOpen} 
                                disabled={!failoverEnabled}
                              >
                                {standbyControllers &&
                                <>
                                  <option key="default" value="">Select...</option>
                                  {standbyControllers.map((standbyController, index) => (
                                      <option key={standbyController.key || index} value={standbyController.props.value}>
                                          {standbyController.props.value}
                                      </option>
                                  ))}
                                </>
                                }
                              </Input>
                            </FormGroup>
                          </CardBody>
                        </Card>
                      </Col>

                      <Col lg={2} md={2} sm={12} xs={12} className="mb-2">
                        <Card className="w-100 h-100 form-section-card">
                          <CardHeader>
                            <div className="w-100 d-flex justify-content-between">
                              <IconHeading icon={<DomainIcon className="icon-medium" />} heading="Tunnel Gateway" />
                            </div>
                          </CardHeader>
                          <CardBody>
                            <FormGroup label="Active Gateway" isRequired={true} fieldId="active-server-toggle" className="form-item"
                              helperTextInvalid=" Required field" helperTextInvalidIcon={<ExclamationCircleIcon />} validated={textInputRequiredOnly(activeGateway.ip4Address)}>
                              <Input
                                type="select" 
                                value={activeGateway.ip4Address || ''}
                                // @ts-ignore
                                onChange={(e, selection, isPlaceholder) => this.onActiveGatewaySelect(e, e.target.value, isPlaceholder)}
                                onClick={(e) => this.onSelectToggle(e, "isActiveGatewayOpen")}
                                open={isActiveGatewayOpen} 
                              >
                                {activeGateways &&
                                <>
                                  <option key="default" value="">Select...</option>
                                  {activeGateways.map((activeGateway, index) => (
                                      <option key={activeGateway.key || index} value={activeGateway.props.value}>
                                          {activeGateway.props.value}
                                      </option>
                                  ))}
                                </>
                                }
                              </Input>
                            </FormGroup>

                            <FormGroup label="Standby Gateway" isRequired={failoverEnabled} fieldId="standby-server-toggle" className="form-item"
                              helperTextInvalid=" Required field" helperTextInvalidIcon={<ExclamationCircleIcon />} validated={failoverEnabled ? textInputRequiredOnly(standbyGateway.ip4Address) : 'default'}>
                              <Input
                                type="select" 
                                value={standbyGateway.ip4Address || ''}
                                // @ts-ignore
                                onChange={(e, selection, isPlaceholder) => this.onStandbyGatewaySelect(e, e.target.value, isPlaceholder)}
                                onClick={(e) => this.onSelectToggle(e, "isStandbyGatewayOpen")}
                                open={isStandbyGatewayOpen} 
                                disabled={!failoverEnabled}
                              >
                                {standbyGateways &&
                                <>
                                  <option key="default" value="">Select...</option>
                                  {standbyGateways.map((standbyGateway, index) => (
                                      <option key={standbyGateway.key || index} value={standbyGateway.props.value}>
                                          {standbyGateway.props.value}
                                      </option>
                                  ))}
                                </>
                                }
                              </Input>
                            </FormGroup>
                          </CardBody>
                        </Card>
                      </Col>

                      <Col lg={2} md={2} sm={12} xs={12} className="mb-2">
                        <Card className="w-100 h-100 form-section-card">
                          <CardHeader>
                              <div className="w-100 d-flex justify-content-between">
                                <div className="w-100">  
                                  <IconHeading 
                                    icon={<LockIcon className="icon-medium" />} heading="Encryption"
                                    showEnable={true} handleEnable={this.handleEncryptionEnabled} enabled={isEncrypted}
                                  />
                                </div>
                              </div>
                          </CardHeader>
                          <CardBody>
                            <p className="font-12">If enabled, all tunnel traffic will be encrypted at 128-bit using a 256-bit key</p>
                          </CardBody>
                        </Card>
                      </Col>

                      <Col lg={2} md={2} sm={12} xs={12} className="mb-2">
                        <Card className="w-100 h-100 form-section-card">
                          <CardHeader>
                              <div className="w-100 d-flex justify-content-between">
                                <IconHeading icon={<ServiceCatalogIcon className="icon-medium" />} heading="ACL Profiles" />
                              </div>
                          </CardHeader>
                          <CardBody>
                            <FormGroup className="form-item">
                              <Select
                                maxHeight={300}
                                chipGroupProps={{ numChips: 1, expandedText: 'Hide', collapsedText: 'Show ${remaining}' }}
                                variant={SelectVariant.typeaheadMulti}
                                typeAheadAriaLabel="Select ACL Profile(s)"
                                onToggle={(e) => this.onSelectToggle(e, 'isACLProfileOpen')}
                                onSelect={this.onACLProfileSelect}
                                onClear={this.clearACLProfileSelection}
                                selections={aclProfiles}
                                isOpen={isACLProfileOpen}
                                aria-labelledby="multi-typeahead-render-chip-group-props-id-1"
                                placeholderText="Select ACL Profile(s)"
                                chipGroupComponent={this.chipGroupComponent()}
                                className="form-control"
                              >
                                {aclProfileOptions.map((option, index) => (
                                  <SelectOption
                                    isDisabled={option.disabled}
                                    key={index}
                                    value={option.value}
                                    {...(option.description && { description: option.description })}
                                  />
                                ))}
                              </Select>
                            </FormGroup>
                          </CardBody>
                        </Card>
                      </Col>
                    </Row>

                  </div>
                </Col>
              </Row>



              {mode === 'edit' && (
                <Row>
                  <Col>
                    <div className="p-3 w-100 h-100 sites-card-wrapper d-flex justify-content-between">
                      <Card isCompact className="mb-3 w-100">
                        <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                            <div className="w-100 d-flex justify-content-between">
                              <IconHeading icon={<TopologyIcon className="icon-medium" />} heading="Site Subnets" /><br />
                            </div>
                        </CardHeader>
                        <CardBody>
                          <Table
                            onRowEdit={this.updateEditableRows}
                            aria-label="Edit IP Addresses"
                            variant={TableVariant.compact}
                            cells={this.getSiteSubnetColumns()}
                            rows={siteSubnets}
                          >
                            <TableHeader />
                            <TableBody />
                          </Table>
                          <br />
                          <span className="right error">{error}</span>
                        </CardBody>
                      </Card>
                    </div>
                  </Col>
                </Row>
              )}

              <Saver 
                submitButtonText="Save" 
                submitButtonisDisabled={formInvalid}
                submit={this.handleSubmit} 
                cancel={this.handleCancel} 
                showCancel={true}
                // @ts-ignore
                message={this.state.message}
                // @ts-ignore
                messageType={this.state.messageType}
              />
            </>
          </Page>
        )}
      </React.Fragment>
    );
  }
}

export { WanForm };
